import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { IFilterOptions } from 'src/apis/types/dropdownAPI';
import { getFetch } from 'src/utils/fetcher';
import { concatQuerys } from 'src/utils/url';

interface IDropdown {
  label: string;
  value: string;
}

interface IDropdownFetch {
  contentUrl: string;
  key: string;
  pageIdentifier: string;
  take: number;
  skip: string;
  queryString?: string;
  searchText?: string;
}

const dropdownFetch = ({
  contentUrl,
  key,
  pageIdentifier,
  queryString,
  searchText,
  skip,
  take,
}: IDropdownFetch): Promise<IFilterOptions> =>
  getFetch({
    path: `/${contentUrl}?pageIdentifier=${pageIdentifier}${queryString ?? ''}${concatQuerys(
      [
        { param: 'searchText', value: searchText },
        { param: 'skip', value: skip },
        { param: 'take', value: String(take) },
      ],
      true,
    )}`,
    key,
  });

interface IUseGetDropDownAPI {
  filterId: string;
  pageIdentifier: string;
  contentUrl?: string;
  queryString?: string;
  searchText?: string;
  take?: number;
}

export const flattenInfiniteFilterData = (data?: InfiniteData<IFilterOptions> | null) => {
  const latestPage = data?.pages[(data?.pages?.length ?? 1) - 1];
  // flatten the array of arrays from the useInfiniteQuery hook
  const entities = data?.pages?.flatMap((page) => page.entities) ?? [];
  const properties = entities?.map((e) => e?.properties ?? { displayText: '', value: '' }) ?? [];

  return {
    dropdownList:
      properties.map<IDropdown>(({ displayText, value }) => ({
        label: displayText,
        value,
      })) ?? [],
    latestPageChildrenLength: latestPage?.entities?.length ?? 0,
  };
};

export const useGetDropdownAPI = ({
  contentUrl = '',
  filterId,
  pageIdentifier,
  queryString,
  searchText,
  take = 50,
}: IUseGetDropDownAPI) => {
  const key = `DropDownFilterInput${filterId}`;

  return useInfiniteQuery<IFilterOptions>({
    enabled: !!contentUrl,
    getNextPageParam: (_lastGroup, groups) => groups.length,
    queryFn: async ({ pageParam = 0 }) =>
      dropdownFetch({
        contentUrl,
        key,
        pageIdentifier,
        queryString,
        searchText,
        skip: String(pageParam * take),
        take,
      }),
    queryKey: [key, { searchText }],
    staleTime: 5000,
  });
};
